import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../services/api';

export interface Project {
  id: string;
  display_id: string;
  name: string;
  slug: string;
  owner_type: 'own' | 'client';
  is_active: boolean;
  role: 'owner' | 'editor' | 'viewer';
  created_at: string;
  updated_at: string;
}

export interface ProjectStats {
  totalPosts: number;
  scheduledPosts: number;
  publishedPosts: number;
  failedPosts: number;
  socialAccounts: number;
}

interface ProjectsState {
  projects: Project[];
  currentProject: Project | null;
  currentStats: ProjectStats | null;
  selectedProjectId: string | null; // Global selected project ID (persisted)
  isLoading: boolean;
  error: string | null;

  fetchProjects: () => Promise<void>;
  fetchProject: (id: string) => Promise<void>;
  fetchProjectStats: (id: string) => Promise<void>;
  createProject: (data: { name: string; owner_type: 'own' | 'client'; slug?: string }) => Promise<Project>;
  updateProject: (id: string, data: Partial<Project>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  setCurrentProject: (project: Project | null) => void;
  setSelectedProjectId: (id: string | null) => void;
  getSelectedProject: () => Project | null;
  clearError: () => void;
}

export const useProjectsStore = create<ProjectsState>()(
  persist(
    (set, get) => ({
      projects: [],
      currentProject: null,
      currentStats: null,
      selectedProjectId: null,
      isLoading: false,
      error: null,

      fetchProjects: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.get<{ data: Project[] }>('/projects');
          set({ projects: response.data, isLoading: false });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Fehler beim Laden der Projekte';
          set({ error: message, isLoading: false });
        }
      },

      fetchProject: async (id: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.get<{ data: Project }>(`/projects/${id}`);
          set({ currentProject: response.data, isLoading: false });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Fehler beim Laden des Projekts';
          set({ error: message, isLoading: false });
        }
      },

      fetchProjectStats: async (id: string) => {
        try {
          const response = await api.get<{ data: ProjectStats }>(`/projects/${id}/stats`);
          set({ currentStats: response.data });
        } catch (error) {
          console.error('Fehler beim Laden der Statistiken:', error);
        }
      },

      createProject: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post<{ data: Project }>('/projects', data);
          const newProject = response.data;
          set((state) => ({
            projects: [newProject, ...state.projects],
            isLoading: false,
          }));
          return newProject;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Fehler beim Erstellen des Projekts';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      updateProject: async (id: string, data: Partial<Project>) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.put<{ data: Project }>(`/projects/${id}`, data);
          const updatedProject = response.data;
          set((state) => ({
            projects: state.projects.map((p) => (p.id === id ? updatedProject : p)),
            currentProject: state.currentProject?.id === id ? updatedProject : state.currentProject,
            isLoading: false,
          }));
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Fehler beim Aktualisieren';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      deleteProject: async (id: string) => {
        set({ isLoading: true, error: null });
        try {
          await api.delete(`/projects/${id}`);
          set((state) => ({
            projects: state.projects.filter((p) => p.id !== id),
            currentProject: state.currentProject?.id === id ? null : state.currentProject,
            selectedProjectId: state.selectedProjectId === id ? null : state.selectedProjectId,
            isLoading: false,
          }));
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Fehler beim Löschen';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      setCurrentProject: (project) => {
        set({ currentProject: project, currentStats: null });
      },

      setSelectedProjectId: (id) => {
        set({ selectedProjectId: id });
      },

      getSelectedProject: () => {
        const { projects, selectedProjectId } = get();
        if (!selectedProjectId) return null;
        return projects.find(p => p.id === selectedProjectId) || null;
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'socialhub-projects',
      partialize: (state) => ({ selectedProjectId: state.selectedProjectId }),
    }
  )
);
